// core/rep-side.mjs — WHICH representative is an outsider, and therefore who the fee is
// split with. PURE, because it decides about MONEY.
//
// Owner report, 2026-09-02, from a live meeting: მაკა — an agent at another agency — was the
// PROPERTY's representative. The co-broke panel never appeared, no ratio was recorded, and
// the whole fee went into our internal split, paying our agents out of money that is half
// hers. The panel asked only about the CLIENT's representative.
//
// The rules live here rather than in the modal for the same reason `cobroke-rules.mjs` does:
// a JSX file cannot be tested, and this answer feeds `commission_amount` → agentDealIncome →
// payroll and the leaderboard. Nothing below renders anything; the modals call it and draw
// what comes back.
//
// ⚠️ EXTERNAL IS ABOUT THE ROSTER, NOT ABOUT THE BASIS (owner 2026-08-06, and it is the one
// mistake this file exists to make impossible). A colleague can be `declared` too — named by
// the person booking rather than resolved from a requirement — and they are still one of
// ours. The scheduler-agent bridge decides: no bridge, no roster. Reading `declared` alone as
// "outsider" would badge a colleague as another agency and offer them as a co-broke
// counterparty, which is a payroll error wearing a co-broke costume.

// Prefer the SERVER's answer, derive only where it is absent.
//
// The server publishes `is_external` for the CLIENT's representative (0449). The PROPERTY
// side has carried the same evidence since 0662 — `resolvePropertyRep` computes
// `isExternal: true` on the write — but its read path still carries 0449's comment saying
// "there is no external tier on this side", so it publishes neither the flag nor the agency.
// The evidence is on the row regardless, and this is the server's own test, so deriving it
// here keeps ONE definition instead of a second copy that drifts.
//
// `??` and not `||`: `is_external: false` is a real answer from the server and must not fall
// through to the derivation.
export function repIsExternal(rep) {
  if (!rep) return false
  if (rep.is_external != null) return !!rep.is_external
  return !!(rep.basis === 'declared' && !rep.agent_id)
}

// WHO the fee is split with, from either slot.
//
// Returns `{ side, party_id, name, agency }` or null. `side` is only ever used to say WHICH
// deal the outsider is on — with one panel now fed by two slots, "50% to მაკა" is otherwise
// ambiguous — and never to change the arithmetic: an outsider's cut comes off the top
// whichever side they represent.
//
// ⚠️ A party id is REQUIRED. A pick held only as a scheduler agent id has no party to name in
// the split, and a split row pointing at nobody is worse than no split: `validateSplit`
// refuses it, so the ratio would silently fail to save after the agent believed it had.
//
// ⚠️ BOTH SIDES EXTERNAL is possible (two outside agencies on one deal) and the client's side
// is offered, deliberately: the editor handles ONE counterparty, `cobroke-rules` allows more,
// and `CobrokeSplit` already renders 3+ rows read-only rather than letting a two-field editor
// rewrite a three-way agreement. Picking a side here beats rendering no panel at all.
export function cobrokeCounterpartyOf(propertyRep, clientRep) {
  const pick = (rep, side) => (repIsExternal(rep) && rep?.party_id
    ? { side, party_id: rep.party_id, name: rep.name || null, agency: rep.agency || null }
    : null)
  return pick(clientRep, 'client') || pick(propertyRep, 'property')
}

// The agents whose scheduler ids may take an INTERNAL share. An outsider has no bridge, so
// `schedulerIdOf` already answers null for them — but saying it out loud on both sides is the
// point: the client side's guard read as the only place the question was asked, which is how
// the property side went unasked for two weeks.
export function internalRepAgentIds(propertyRep, clientRep, schedulerIdOf) {
  return [
    repIsExternal(propertyRep) ? null : schedulerIdOf(propertyRep),
    repIsExternal(clientRep) ? null : schedulerIdOf(clientRep),
  ].filter(Boolean)
}

// ── THE 15-MINUTE CORRECTION WINDOW (owner ruling 2026-09-02) ─────────────────────────────
// "allow edits for short fixed period on agents; manager and admin can edit anytime, but
// logged." The logging half already existed — every one of these writes is evented and the
// meeting history reads them back — so what was missing was the clock.
//
// ⚠️ THE SERVER IS THE AUTHORITY. `core_meeting.js`'s PATCH refuses a late edit with
// `edit_window_closed` whatever this returns; everything here decides only whether to DRAW
// the control, so that an agent is never offered a pencil that will 403 at them. The two
// judge by the SAME evidence — the meeting's own event stream — which is why this takes the
// history the modal has already fetched rather than a second, differently-derived answer.
export const EDIT_WINDOW_MS = 15 * 60 * 1000

// Roles that edit without a clock. Mirrors core_visibility.js's MGMT_ROLES; a copy, because
// the browser has no other source for it — and a cosmetic one, since the server re-decides.
const MGMT_ROLES = new Set(['admin', 'manager', 'general_manager'])
export const isMgmtRole = role => MGMT_ROLES.has(role)

// When the clock for one field last STARTED: the last time it changed, or the meeting's own
// creation when it never has.
//
// `kind`: 'property' | 'client' (the representative slots) | 'split' (the co-broke ratio).
// Each representative slot keeps its own clock — correcting the property side must not
// consume the client side's window — which is why the rep events carry `side`.
export function correctionWindowStart(history, kind, meetingCreatedAt) {
  const verb = kind === 'split' ? 'meeting.commission_split_set' : 'meeting.representative_changed'
  const rows = (Array.isArray(history) ? history : [])
    .filter(e => e.kind !== 'comment' && e.verb === verb && (kind === 'split' || e.side === kind))
    .map(e => e.at)
    .filter(Boolean)
    .sort()
  return rows.length ? rows[rows.length - 1] : (meetingCreatedAt || null)
}

// May this viewer still correct that field, and until when?
//
// ⚠️ FILLING AN EMPTY FIELD IS NOT AN EDIT. `hasValue: false` is always open — a meeting
// booked with no client representative is a record waiting to be completed, and refusing to
// complete it an hour later would make that gap permanent. The server exempts it identically.
//
// ⚠️ UNKNOWN IS OPEN, never closed. A history that failed to load (`false`/null) must not
// lock the record: the agent would be told to ask a manager about a limit whose start nobody
// can see. The server refuses regardless, so the worst case is one honest 403.
export function correctionWindow(
  { history, kind, meetingCreatedAt, role, hasValue = true, now = Date.now() } = {}) {
  if (isMgmtRole(role)) return { open: true, unlimited: true, until: null }
  if (!hasValue) return { open: true, unlimited: true, until: null }
  if (!Array.isArray(history)) return { open: true, unlimited: false, until: null }
  const since = correctionWindowStart(history, kind, meetingCreatedAt)
  if (!since) return { open: true, unlimited: false, until: null }
  const started = Date.parse(since)
  if (!Number.isFinite(started)) return { open: true, unlimited: false, until: null }
  const until = started + EDIT_WINDOW_MS
  return { open: now <= until, unlimited: false, until: new Date(until).toISOString() }
}
